'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';

import { MenuItem } from '@/lib/drupal';
import { localizedHref } from '@/lib/locale';
import LanguageSelector, { Language } from '../../ui/LanguageSelector';
import { Icon } from '../../ui/Icon';

interface FooterContentProps {
  lang: string;
  menuItems: MenuItem[];
  socialLinks: MenuItem[];
  languages: Language[];
}

export default function FooterContent({ lang, menuItems, socialLinks, languages }: FooterContentProps) {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const half = Math.ceil(menuItems.length / 2);
  const leftCol = menuItems.slice(0, half);
  const rightCol = menuItems.slice(half);

  return (
    <>
      <footer className="Footer mt-[30px] overflow-hidden">
        <div className="site-container border-t border-[#D9D9D9]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="bg-[#cbd5e1]"
          >
            <div className="footer-container py-[60px] lg:pt-20 lg:pb-5">
              <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between w-full gap-12 lg:gap-0">
                {/* Logo */}
                <div className="flex justify-center lg:justify-start">
                  <Link href={localizedHref(lang)}>
                    <img src="/assets/logo.svg" alt="Agathos" width={95} height={81} className="h-12 lg:h-16 w-auto" />
                  </Link>
                </div>

                {/* Footer Menu */}
                <nav aria-label="navigation" className="flex gap-10 lg:gap-[125px] justify-center text-center lg:text-left">
                  <div className="flex flex-col gap-4 lg:gap-5">
                    {leftCol.map((item) => (
                      <Link key={item.title} href={item.url} className="text-[#363B48] text-sm lg:text-lg hover:text-brand-secondary transition-colors font-medium lg:font-normal">
                        {item.title}
                      </Link>
                    ))}
                  </div>
                  <div className="flex flex-col gap-4 lg:gap-5">
                    {rightCol.map((item) => (
                      <Link key={item.title} href={item.url} className="text-[#363B48] text-sm lg:text-lg hover:text-brand-secondary transition-colors font-medium lg:font-normal">
                        {item.title}
                      </Link>
                    ))}
                  </div>
                </nav>

                {/* Language & Socials Links */}
                <div className="flex flex-col items-center lg:items-start gap-8 lg:pl-20 xl:pl-[125px] relative">
                  <motion.div
                    initial={{ height: 0 }}
                    whileInView={{ height: '100%' }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 1 }}
                    aria-hidden="true"
                    className="hidden lg:block absolute left-0 top-0 w-[1px] bg-[#0A4492]/20"
                  />

                  <LanguageSelector variant="footer" languages={languages} />

                  {/* Social Links */}
                  <div className="flex flex-col items-center lg:items-start gap-4 lg:gap-5" role="list" aria-label="Social links">
                    {socialLinks.map((social) => (
                      <Link
                        key={social.title}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        role="listitem"
                        aria-label={`${social.title} (opens in new tab)`}
                        className="text-[14px] lg:text-[15px] text-[#363B48] hover:text-[#0A4492] transition-colors"
                      >
                        {social.title}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Legal Links & Copyright */}
              <div className="mt-8 pt-5 border-t border-[#0A4492]/10 flex flex-col items-center gap-3">
                <div className="flex gap-4">
                  <Link href={localizedHref(lang, '/privacy-policy')} className="text-[10px] lg:text-xs tracking-wider text-[#363B48]/60 hover:text-brand-secondary transition-colors uppercase font-bold">
                    {lang === 'es' ? 'Privacidad' : 'Privacy Policy'}
                  </Link>
                  <span className="text-[#363B48]/30">|</span>
                  <Link href={localizedHref(lang, '/terms-of-service')} className="text-[10px] lg:text-xs tracking-wider text-[#363B48]/60 hover:text-brand-secondary transition-colors uppercase font-bold">
                    {lang === 'es' ? 'Términos' : 'Terms of Service'}
                  </Link>
                </div>
                <p className="text-[10px] lg:text-xs tracking-[0.2em] text-[#363B48]/60 uppercase font-bold">
                  © 2025—{new Date().getFullYear()} Designed by Agathos
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </footer>

      {/* Floating back-to-top button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-brand-primary text-white shadow-lg flex items-center justify-center hover:bg-brand-secondary transition-colors"
            aria-label="Back to top"
          >
            <Icon name="ChevronUp" size={22} />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}
